import React, { useCallback, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useMutation } from "@apollo/client";

import { DELETE_EMOJI, UPDATE_EMOJI, UPLOAD_EMOJI_ASSET } from "../../../components/apolloQueries";
import { 
  Emoji,
  DeleteEmojiMutation,
  DeleteEmojiMutationVariables,
  UpdateEmojiMutation,
  UpdateEmojiMutationVariables, 
  UploadEmojiAssetMutation,
  UploadEmojiAssetMutationVariables,
  EmojiAvailabilityType} from "../../../../../../__gqltypes__";
import DeleteItemModal from "./DeleteItemModal";
import DragUpload from "./DragUpload";
import { ClipboardButton } from "../../../../curation/screens/SongSearch/components/ClipboardButton";
import ExpandableFormControl from "../../../components/ExpandableFormControl";
import { validateUtfCode } from "./EmojiFactory";

type Props = {
  item: Emoji;
  index: number;
  isNew: boolean;
  emojiPackSize: number;
  source?: string | null;
  onUpdate: () => void;
  onDelete: (index: number) => void;
  onMove: (index: number, isUp: boolean) => void;
  availability: EmojiAvailabilityType;
}

const avaiTexts: Record<EmojiAvailabilityType, string> = {
  [EmojiAvailabilityType.DD]: 'Daily Deals',
  [EmojiAvailabilityType.FREE]: 'Free',
  [EmojiAvailabilityType.SUB]: 'Free with subscription',
  [EmojiAvailabilityType.EARN]: 'Earn in game',
};

export default function EmojiView({
  item,
  index,
  isNew,
  emojiPackSize,
  source = null,
  onUpdate,
  onDelete,
  onMove,
  availability
}: Props): JSX.Element {
  const [itemData, setItemData] = useState<Emoji>(item);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  
  const [updateEmoji, {loading: updateLoading}] = useMutation<
    UpdateEmojiMutation,
    UpdateEmojiMutationVariables
  >(UPDATE_EMOJI);

  const [deleteEmoji, { loading: deleteLoading}] = useMutation<
    DeleteEmojiMutation,
    DeleteEmojiMutationVariables
  >(DELETE_EMOJI);

  const [uploadEmoji, { loading: uploadLoading}] = useMutation<
    UploadEmojiAssetMutation,
    UploadEmojiAssetMutationVariables
  >(UPLOAD_EMOJI_ASSET);

  const handleUpdate = useCallback((newData: Emoji) => {
    updateEmoji({ variables: {
      input: {
        id: newData.id,
        name: newData.name,
        asset: newData.asset,
        utfCode: newData.utfCode,
      }
    }}).then(() => {
      onUpdate();
    });
  }, [updateEmoji, onUpdate]);

  const handleDelete = useCallback(() => {
    deleteEmoji({variables: {
      id: itemData.id,
    }}).then(() => {
      onDelete(index);
    });
  }, [deleteEmoji, index, itemData.id, onDelete]);

  const handleUpload = useCallback((file: Blob) => {
    const file_ = file as File;
    const codePoint_ = file_.name.codePointAt(0) ?? 0; 
    const codePoint = codePoint_.toString(16);
    const sliceStr = file_.name.slice(file_.name.indexOf('_'));
    const newName = codePoint.concat(sliceStr);
    const newFile = new File([file], newName);
    uploadEmoji({variables: {
      input: {
        file: newFile,
      }
    }}).then(({data}) => {
      const newData = {...itemData, asset: data?.uploadEmojiAsset.asset ?? ''};
      handleUpdate(newData);
    });
  }, [handleUpdate, itemData, uploadEmoji]);
  const loading = updateLoading || deleteLoading || uploadLoading;
  const isValidUtfCode = !isNew || validateUtfCode(itemData.utfCode);

  return (
    <>
      <Card className="mt-3" style={styles.cardContainer}>
      <DeleteItemModal
          title={`Delete "${itemData?.name}" Emoji?`}
          show={showDeleteModal && !loading}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Card.Body style={styles.bodyContainer}>
          <Row className="mb-3 align-items-end">
            <Form.Group as={Col}>
              <Form.Label>Id</Form.Label>
              <Form.Control placeholder={itemData.id} disabled />
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Form.Label>Name</Form.Label>
              <Form.Control
                style={styles.nameContainer}
                placeholder=""
                value={itemData.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Form.Label >UTF code</Form.Label>
              <Form.Control
                style={{
                  ...styles.nameContainer,
                  backgroundColor: !isValidUtfCode ? 'red' : undefined
                }}
                placeholder=""
                value={itemData.utfCode}
                disabled={!isNew}
                onChange={(e) => {
                  const utfCode = e.target.value;
                  const newId = `${itemData.id.split('.')[0]}.${utfCode}`;
                  setItemData({ ...itemData, utfCode, id: newId });
                }}
              />
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Form.Label >Source</Form.Label>
              <Form.Control
                style={styles.avaiContainer}
                placeholder=""
                value={avaiTexts[availability]}
                disabled={true}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
                Delete
              </Button>
              <Button style={{marginRight: 10}} onClick={() => handleUpdate(itemData)}>Save</Button>
              {index === 0 ? null : (
                <Button 
                  size="sm" 
                  variant="light" 
                  onClick={() => onMove(index, true)} 
                  style={{borderWidth: 1, borderColor: 'rgba(127, 127, 127, 0.4)', marginRight : 4}}>
                  <i className="fas fa-angle-up" />
                </Button>
              )}
              {index === emojiPackSize - 1 ? null : (
                <Button 
                  size="sm"
                  variant="light"
                  onClick={() => onMove(index, false)}
                  style={{borderWidth: 1, borderColor: 'rgba(127, 127, 127, 0.4)'}}>
                  <i className="fas fa-angle-down" />
                </Button>
              )}
            </Form.Group>
          </Row>
          <Row className="align-items-end">
            {!!itemData && (
              <Form.Group as={Col} xs="auto">
                <img 
                  src={source || itemData.asset}
                  alt={itemData.name}
                  style={styles.img}/>
              </Form.Group>
            )}
            <Form.Group as={Col} xs="auto">
              <Form.Label>Asset</Form.Label>
              <ExpandableFormControl
                placeholder=""
                value={itemData.asset}
                disabled
                expandedWidth={550}
              />
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <ClipboardButton title="Copy URL" textToCopy={itemData.asset} />
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Button onClick={() => setShowUpload(val => !val)}>
                Upload new asset <i className="fas fa-upload" />
              </Button>
            </Form.Group>
          </Row>
          {showUpload && (<DragUpload onUpload={handleUpload} />)}
        </Card.Body>      
      </Card>
    </>
  );
}

const styles: {[key:string]: React.CSSProperties } = {
  cardContainer: {
    borderWidth: 4,
    borderColor: 'rgba(127, 127, 127, 0.4)',
    borderRadius: 10, 
  },
  bodyContainer: {
    height: '100%',
  },
  nameContainer: {
    width: 100,
  },
  avaiContainer: {
    width: 190,
  },
  img: {
    display: 'block',
    width: 64,
    height: 64,
  },
};